import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

// Shapes written by the ai-top-matches edge function (job_ai_shortlists row)

type AITopMatchesStatus = "queued" | "running" | "completed" | "failed" | "cancelled";
export type AITopMatchesStage = "plan" | "screen" | "scout" | "deep" | "explain" | "done";
export type AITopMatchesVerdict = "strong" | "possible" | "weak" | "no";

interface AITopMatchesCriterion {
	id: string;
	label: string;
	evidence_terms: string[];
	weight: number;
}

export interface AITopMatchesBrief {
	role_summary: string;
	title_synonyms: string[];
	seniority: { level: string; min_years: number | null; max_years: number | null };
	must_haves: AITopMatchesCriterion[];
	nice_to_haves: AITopMatchesCriterion[];
	deal_breakers: string[];
	languages: { code: string; min_cefr: number; mandatory: boolean }[];
	location: { mode: string; place: string | null; notes: string | null };
	availability: string | null;
	search_terms: string[];
	scoring_notes: string;
}

export interface AITopMatchesCriterionResult {
	id: string;
	label: string;
	p: number | null;
	status: "met" | "partial" | "missing" | "unknown";
	evidence: string | null;
}

export interface AITopMatchesItem {
	candidate_id: string;
	rank: number;
	// Rank of an earlier result with the same name (likely duplicate candidate record)
	duplicate_of_rank?: number | null;
	score: number;
	verdict: AITopMatchesVerdict;
	screen_score: number | null;
	source: "screen" | "scout" | "all";
	scout_reason: string | null;
	fit: number | null;
	must_haves: AITopMatchesCriterionResult[];
	nice_to_haves: AITopMatchesCriterionResult[];
	flags: string[];
	summary: string;
	strengths: string[];
	gaps: string[];
	availability_note: string | null;
}

interface AITopMatchesResults {
	items: AITopMatchesItem[];
	coverage: { id: string; label: string; met: number }[];
	generated_at: string;
}

export interface AITopMatchesStats {
	model?: string;
	decider?: "decisions" | "chat";
	pool_estimate?: number;
	excluded_in_pipeline?: number;
	screened?: number;
	screen_failed?: number;
	nominated?: number;
	deep_checked?: number;
	final_count?: number;
	slices?: number;
	cost_usd?: number;
	duration_ms?: number;
	scout_trace?: { tool: string; args: { terms?: string[]; match?: string; why?: string }; count: number }[];
	scout_notes?: string | null;
	warnings?: string[];
}

export interface AITopMatchesRun {
	id: string;
	job_id: string;
	status: AITopMatchesStatus;
	stage: AITopMatchesStage;
	progress: { stage?: AITopMatchesStage; done?: number; total?: number; screened?: number };
	instructions: string | null;
	options: { excludeStatuses?: string[] };
	brief: AITopMatchesBrief | null;
	results: AITopMatchesResults | null;
	stats: AITopMatchesStats;
	error: string | null;
	created_at: string;
	completed_at: string | null;
	heartbeat_at: string | null;
	lease_until: string | null;
}

export interface AITopMatchesCandidate {
	id: string;
	name: string | null;
	function: string | null;
	location: string | null;
	status: string | null;
	unavailable_until: string | null;
}

// Never select the internal `state` checkpoint column (large)
const RUN_COLUMNS = "id, job_id, status, stage, progress, instructions, options, brief, results, stats, error, created_at, completed_at, heartbeat_at, lease_until";
const POLL_MS = 2000;
const STALE_MS = 150_000;

export const isRunActive = (run: AITopMatchesRun | null) => !!run && (run.status === "queued" || run.status === "running");

export const AI_TOP_MATCHES_STAGES: AITopMatchesStage[] = ["plan", "screen", "scout", "deep", "explain"];

/** Position in the pipeline and overall completion (0-100) of a run. */
export const getRunProgress = (run: AITopMatchesRun) => {
	const currentIndex = AI_TOP_MATCHES_STAGES.indexOf(run.stage === "done" ? "explain" : run.stage);
	const done = run.progress?.done ?? 0;
	const total = run.progress?.total ?? 0;
	const counted = (run.stage === "screen" || run.stage === "deep") && total > 0;
	const stageFraction = counted ? Math.min(1, done / total) : 0;
	const overall = Math.round(((Math.max(0, currentIndex) + stageFraction) / AI_TOP_MATCHES_STAGES.length) * 100);
	return { currentIndex, done, total, overall };
};

const isRunStale = (run: AITopMatchesRun | null) => {
	if (!run || !isRunActive(run))
		return false;
	const heartbeat = new Date(run.heartbeat_at ?? run.created_at).getTime();
	const leaseExpired = !run.lease_until || new Date(run.lease_until).getTime() < Date.now();
	return Date.now() - heartbeat > STALE_MS && leaseExpired;
};

export const useAITopMatches = (jobId: string | undefined, active: boolean) => {
	const { t } = useTranslation();
	const [run, setRun] = useState<AITopMatchesRun | null>(null);
	const [loading, setLoading] = useState(false);
	const [starting, setStarting] = useState(false);
	const [adding, setAdding] = useState(false);
	const [candidatesById, setCandidatesById] = useState<Record<string, AITopMatchesCandidate>>({});
	const resumedRunRef = useRef<string | null>(null);

	const fetchRun = useCallback(async (runId: string) => {
		const { data } = await (supabase as any)
			.from("job_ai_shortlists")
			.select(RUN_COLUMNS)
			.eq("id", runId)
			.maybeSingle();
		if (data)
			setRun(data as AITopMatchesRun);
		return data as AITopMatchesRun | null;
	}, []);

	// Latest run of the job when the modal opens
	useEffect(() => {
		if (!active || !jobId)
			return;

		let cancelled = false;
		const loadLatest = async () => {
			setLoading(true);
			const { data } = await (supabase as any)
				.from("job_ai_shortlists")
				.select(RUN_COLUMNS)
				.eq("job_id", jobId)
				.order("created_at", { ascending: false })
				.limit(1)
				.maybeSingle();
			if (!cancelled)
				setRun((data as AITopMatchesRun) ?? null);
			if (!cancelled)
				setLoading(false);
		};
		loadLatest();
		return () => {
			cancelled = true;
		};
	}, [active, jobId]);

	// Poll while the run is in progress; restart it once if its worker stalled
	const runId = run?.id;
	const runActive = isRunActive(run);
	useEffect(() => {
		if (!active || !runId || !runActive)
			return;

		const interval = setInterval(async () => {
			const latest = await fetchRun(runId);
			if (latest && isRunStale(latest) && resumedRunRef.current !== runId) {
				resumedRunRef.current = runId;
				supabase.functions.invoke("ai-top-matches", { body: { action: "resume", runId } });
			}
		}, POLL_MS);
		return () => clearInterval(interval);
	}, [active, runId, runActive, fetchRun]);

	// Candidate names / functions for the final list (RLS-scoped)
	const resultIds = (run?.status === "completed" ? run.results?.items ?? [] : []).map((item) => item.candidate_id).join(",");
	useEffect(() => {
		if (!resultIds)
			return;

		let cancelled = false;
		const loadCandidates = async () => {
			const { data } = await supabase
				.from("candidates")
				.select("id, name, function, location, status, unavailable_until")
				.in("id", resultIds.split(","));
			if (cancelled || !data)
				return;
			const byId: Record<string, AITopMatchesCandidate> = {};
			for (const candidate of data as AITopMatchesCandidate[])
				byId[candidate.id] = candidate;
			setCandidatesById(byId);
		};
		loadCandidates();
		return () => {
			cancelled = true;
		};
	}, [resultIds]);

	const startRun = useCallback(async (instructions: string, excludeStatuses: string[]) => {
		if (!jobId)
			return;

		setStarting(true);
		try {
			const { data, error } = await supabase.functions.invoke("ai-top-matches", {
				body: { action: "start", jobId, instructions, options: { excludeStatuses } },
			});
			if (error || !data?.runId)
				throw error ?? new Error("No run id");
			resumedRunRef.current = null;
			await fetchRun(data.runId);
			toast({ title: t("aiTopMatches.toasts.started"), description: t("aiTopMatches.toasts.startedDescription") });
		} catch {
			toast({ title: t("errors.error"), description: t("aiTopMatches.toasts.startError"), variant: "destructive" });
		} finally {
			setStarting(false);
		}
	}, [jobId, fetchRun, t]);

	const cancelRun = useCallback(async () => {
		if (!run)
			return;
		await supabase.functions.invoke("ai-top-matches", { body: { action: "cancel", runId: run.id } });
		await fetchRun(run.id);
		toast({ title: t("aiTopMatches.toasts.cancelled") });
	}, [run, fetchRun, t]);

	const resumeRun = useCallback(async () => {
		if (!run)
			return;
		resumedRunRef.current = run.id;
		await supabase.functions.invoke("ai-top-matches", { body: { action: "resume", runId: run.id } });
		await fetchRun(run.id);
	}, [run, fetchRun]);

	/** Adds the candidates to the job pipeline and queues their full match analysis. Returns how many were added. */
	const addCandidates = useCallback(async (candidateIds: string[]): Promise<number> => {
		if (!jobId || candidateIds.length === 0)
			return 0;

		setAdding(true);
		try {
			const { data: existingMatches, error: checkError } = await supabase
				.from("matches")
				.select("candidate_id")
				.eq("job_id", jobId)
				.in("candidate_id", candidateIds);
			if (checkError)
				throw checkError;

			const existingIds = new Set((existingMatches ?? []).map((m) => m.candidate_id));
			const inserts = candidateIds
				.filter((id) => !existingIds.has(id))
				.map((id) => ({ job_id: jobId, candidate_id: id, score: 0, status: "matched" }));

			if (inserts.length === 0) {
				toast({ title: t("aiTopMatches.toasts.allAlreadyInPipeline") });
				return 0;
			}

			const { data, error } = await supabase.from("matches").insert(inserts).select("id");
			if (error)
				throw error;

			// full-match-v2: same analysis as the queued full-match, with explicit prompt caching
			// and without the science report (see docs/AI_TOP_MATCHES.md)
			await Promise.all((data ?? []).map((match) =>
				supabase.functions.invoke("full-match-v2?command=queue", {
					method: "POST",
					body: { matchId: match.id },
				})
			));

			toast({ title: t("aiTopMatches.toasts.added", { count: inserts.length }) });
			return inserts.length;
		} catch {
			toast({ title: t("errors.error"), description: t("aiTopMatches.toasts.addError"), variant: "destructive" });
			return 0;
		} finally {
			setAdding(false);
		}
	}, [jobId, t]);

	return {
		run,
		loading,
		starting,
		adding,
		candidatesById,
		isStale: isRunStale(run),
		startRun,
		cancelRun,
		resumeRun,
		addCandidates,
	};
};
